import { ref } from 'vue';
import type {
	ToastAction,
	ToastPosition,
	ToastType,
	ToastConfig,
} from './Toast.model';
import { ToastConfigDefault } from './Toast.model';

export interface ToastOptions {
	message: string;
	title?: string;
	icon?: string;
	duration?: number;
	position?: ToastPosition;
	type?: ToastType;
	dismissible?: boolean;
	onClose?: () => void;
	id?: string;
	action?: ToastAction;
}

export interface ToastInstance extends ToastConfig {
	id: string;
	openedTime: number;
	state: {
		closing: boolean;
		closeRevision: number;
		timerId: ReturnType<typeof setTimeout> | null;
		remaining: number;
		resumedAt: number;
	};
	onClose?: () => void;
}

const useToastService = () => {
	const toasts = ref<ToastInstance[]>([]);

	const showToast = (opts: ToastOptions) => {
		const options = { ...ToastConfigDefault, ...opts };
		const id = options.id || crypto.randomUUID();

		const newToast: ToastInstance = {
			id,
			message: options.message,
			title: options.title,
			icon: options.icon,
			duration: options.duration,
			position: options.position,
			type: options.type,
			dismissible: options.dismissible,
			action: options.action,
			onClose: options.onClose,
			openedTime: Date.now(),
			state: {
				closing: false,
				closeRevision: 0,
				timerId: null,
				remaining: options.duration ?? 0,
				resumedAt: Date.now(),
			},
		};

		toasts.value.push(newToast);

		if (options.duration && options.duration > 0) {
			newToast.state.timerId = setTimeout(() => {
				hideToast(id);
			}, options.duration);
		}

		return id;
	};

	const pauseToast = (id: string) => {
		const toast = toasts.value.find((t) => t.id === id);
		if (!toast || !toast.state.timerId) {
			return;
		}
		clearTimeout(toast.state.timerId);
		toast.state.timerId = null;
		toast.state.remaining -= Date.now() - toast.state.resumedAt;
	};

	const resumeToast = (id: string) => {
		const toast = toasts.value.find((t) => t.id === id);
		if (!toast || toast.state.timerId || toast.state.remaining <= 0) {
			return;
		}
		toast.state.resumedAt = Date.now();
		toast.state.timerId = setTimeout(() => {
			hideToast(id);
		}, toast.state.remaining);
	};

	const hideToast = (id: string) => {
		const toast = toasts.value.find((t) => t.id === id);
		if (toast) {
			if (toast.state.closing) {
				return;
			}
			if (toast.state.timerId) {
				clearTimeout(toast.state.timerId);
				toast.state.timerId = null;
			}
			toast.state.closing = true;
			toast.state.closeRevision += 1;
			const revision = toast.state.closeRevision;
			setTimeout(() => {
				const activeToast = toasts.value.find((t) => t.id === id);
				if (!activeToast || activeToast.state.closeRevision !== revision) {
					return;
				}
				toast.onClose?.();
				toasts.value = toasts.value.filter((t) => t.id !== id);
			}, 300); // Short animation duration for closing
		}
	};

	const hideAllToasts = () => {
		toasts.value.forEach((toast) => hideToast(toast.id));
	};

	const clear = () => {
		toasts.value.forEach((toast) => toast.onClose?.());
		toasts.value = [];
	};

	return {
		toasts,
		show: showToast,
		hide: hideToast,
		pause: pauseToast,
		resume: resumeToast,
		hideAll: hideAllToasts,
		clear,
	};
};

export const toastService = useToastService();
export type ToastService = ReturnType<typeof useToastService>;
